import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Router from "next/router";
import { capitalize } from "lodash-es";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import KButton from "@kikoff/components/src/v1/buttons/KButton";
import Card from "@kikoff/components/src/v1/cards/Card";
import KLink from "@kikoff/components/src/v1/navigation/KLink";
import Strike from "@kikoff/components/src/v1/text/Strike";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import useStateOrBind from "@kikoff/hooks/src/useStateOrBind";
import { web } from "@kikoff/proto/src/protos";
import { combineClasses, format } from "@kikoff/utils/src/string";

import PageAlign from "@component/dashboard/offer/page_align";
import PhoneDuringOnboardingSection from "@component/dashboard/store/phone_during_onboarding_section";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import Line from "@component/line";
import StreamlinedPaymentPromoCard from "@component/onboarding/promo";
import StreamlinedHeader from "@component/onboarding/streamlined_header";
import { FREE_BASIC_DISCOUNTED_PREMIUM } from "@constant/promos";
import { selectExperimentVariant } from "@feature/experiment";
import { selectFeatureFlag, selectShowPhoneSupport } from "@feature/page";
import {
  fetchCheckoutPreview,
  selectCheckoutPreview,
  selectProductForPlan,
  updateBag,
  updateCatalog,
} from "@feature/shopping";
import { Plan } from "@feature/subscription";
import { selectCheckoutPromos, selectPromo } from "@feature/user";
import { getStoreLowercaseMentalModelName } from "@src/constants";
import { useOverlaysController } from "@src/overlay";
import analytics, { track } from "@util/analytics";
import { Events } from "@util/events";
import { l10nResources } from "@util/l10n";

import { Goal, goals } from "../../[[...storeRoute]]";
import check from "../images/verified.svg";

import styles from "./styles.module.scss";

const l10n = l10nResources({
  en: `select-plan-title = Select your plan
credit-plus-select-plan-title = Select your Credit+ Plan
membership-select-plan-title = Select your membership
both-plans-desc = Choose one of two plans that both help build your credit with 0% interest, no credit checks, and no hidden fees.
build-goal-shortname = Build Credit
borrow-goal-shortname = Get Cash
basic-plan-shortname = Basic
premium-plan-shortname = Premium
ultimate-plan-shortname = Ultimate
monthly-price-title = Monthly price
monthly-price-title-promo = Special offer on your 1st mo*
credit-reports-scores-title = Credit reports & scores
one-bureau = 1 bureau
three-bureaus = 3 bureaus
build-payment-history-title = Build payment history
lower-credit-utilization-title = Lower your credit utilization
rent-reporting-benefit-title = Build credit with rent payments
rent-reporting-future-benefit-title = Build credit with future rent payments
rent-reporting-past-benefit-title = Build credit with past rent payments
reported-credit-line-title = Reported Tradeline
identity-theft-insurance-title = Identity theft insurance up to $1M
personal-data-protection-title = Personal data protection
personal-data-protection-subtitle = Automatically remove your info from the web and prevent financial fraud, identity scams, and robocalls & texts
credit-line-disclaimer = Tradelines are for lowering utilization and making purchases on Kikoff products and services to build positive payment history and not utilizable outside of Kikoff.
continue-button = Continue
yes = Yes
free = Free
disputes-mailing-cost = Send dispute letters to Equifax
privacy-protection = Privacy protection
duration-desc = Credit Service is a 12-month plan with no credit checks or hidden fees. Pick one that fits best to your needs.
credit-plus-duration-desc = Credit+ is a 12-month plan with no credit checks or hidden fees. Pick one that fits best to your needs.
membership-duration-desc = Get your 12-month Kikoff membership with no credit checks or hidden fees. Pick one that fits best to your needs.
dispute-desc = Both plans help you build payment history and lower your credit utilization
debt-desc = A debt consolidation gives you a revolving line of credit that pays off your cards and turns it into one simply monthly bill.
loc-desc = The general purpose line of credit can be used for every day purchases. You can withdraw funds to your bank account and pay back accordingly.
terms-and-conditions = Terms and Conditions
store-disclaimer = Kikoff has a number of credit building items and educational items available for purchase in our
money-back-guarantee-box-text = 45-day money back guarantee
see = See
see-more = See more
see-less = See less
continue = Continue
interest-rate = Interest rate
credit-limit = Credit limit
up-to = Up to
automatically-pays-eligible-cards = Automatically pays eligible cards
credit-scores-and-reports = Credit scores and reports
`,
  es: `select-plan-title = Selecciona tu plan
credit-plus-select-plan-title = Selecciona tu plan
membership-select-plan-title = Selecciona tu plan
both-plans-desc = Elija uno de los dos planes que le ayudarán a construir su crédito con 0% de interés, sin verificaciones de crédito y sin cargos ocultos.
build-goal-shortname = Construir Crédito
borrow-goal-shortname = Conseguir Dinero
basic-plan-shortname = Básica
premium-plan-shortname = Premium
ultimate-plan-shortname = Ultimate
monthly-price-title = Precio mensual
monthly-price-title-promo = Oferta especial en tu 1er mes*
credit-reports-scores-title = Informes y puntajes de crédito
one-bureau = 1 buró de crédito
three-bureaus = 3 burós de crédito
build-payment-history-title = Crear historial de pago
lower-credit-utilization-title = Reduzca la utilización de su crédito
rent-reporting-benefit-title = Construya crédito con pagos de alquiler
rent-reporting-future-benefit-title = Genere crédito con futuros pagos de alquiler
rent-reporting-past-benefit-title = Genere crédito con pagos de alquiler anteriores
reported-credit-line-title = Línea de crédito informada
identity-theft-insurance-title = Seguro contra robo de identidad de hasta $1 millón
personal-data-protection-title = Protección de datos personales
personal-data-protection-subtitle = Elimine automáticamente su información de la web y evite fraudes financieros, estafas de identidad y llamadas automáticas y mensajes de texto
credit-line-disclaimer = Las líneas de crédito son para reducir la utilización y realizar compras de productos y servicios de Kikoff para crear un historial de pago positivo y no se pueden utilizar fuera de Kikoff.
continue-button = Continuar
yes = Sí
disputes-mailing-cost = Envío de cartas de disputa a Equifax
privacy-protection = Protección de la privacidad
free = Gratis
duration-desc = El servicio de crédito es un plan de 12 meses sin verificaciones de crédito ni cargos ocultos. Elija uno que mejor se adapte a sus necesidades.
credit-plus-duration-desc = El servicio de crédito es un plan de 12 meses sin verificaciones de crédito ni cargos ocultos. Elija uno que mejor se adapte a sus necesidades.
membership-duration-desc = El servicio de crédito es un plan de 12 meses sin verificaciones de crédito ni cargos ocultos. Elija uno que mejor se adapte a sus necesidades.
dispute-desc = Ambos planes lo ayudan a crear un historial de pagos y reducir la utilización de su crédito.
debt-desc = Una consolidación de deuda le brinda una línea de crédito renovable que liquida sus tarjetas y la convierte en una simple factura mensual.
loc-desc = La línea de crédito de propósito general se puede utilizar para compras diarias. Puede retirar fondos a su cuenta bancaria y pagar en consecuencia.
terms-and-conditions = Términos y condiciones
store-disclaimer = Kikoff tiene varios elementos para la creación de crédito y artículos educativos disponibles en nuestro
money-back-guarantee-box-text = Garantía de devolución de dinero de 45 días
see = Ver
see-more = Ver más
see-less = Ver menos
continue = Continuar
interest-rate = Tasa de interés
credit-limit = Límite de crédito
up-to = Hasta
automatically-pays-eligible-cards = Paga automáticamente tarjetas elegibles
credit-scores-and-reports = Informes y puntajes de crédito
`,
});

function StoreLink() {
  return (
    <KLink
      variant="underlined"
      data-cy="store-catalog"
      href="./catalog"
      onClick={() => {
        track.tap("CA - Activation - Select Another Item");
      }}
    >
      {getStoreLowercaseMentalModelName()}
    </KLink>
  );
}

function TcLink() {
  return (
    <KLink variant="underlined" href="https://kikoff.com/terms.pdf" type="pdf">
      {l10n.getString("terms-and-conditions")}
    </KLink>
  );
}

export function StoreDisclaimer() {
  return (
    <span className={`text:mini ${styles["select-product-footer-text"]}`}>
      {l10n.getString("store-disclaimer")} <StoreLink />.{" "}
      {l10n.getString("see")} <TcLink />
    </span>
  );
}

export function MoneyBackGuarantee() {
  const overlays = useOverlaysController();

  return (
    <ContainerButton
      onClick={() => {
        overlays.push("credit_line/money_back_guarantee", {});
      }}
    >
      <Card className="bg:primary-dugout">
        <div className={`text:regular+ ${styles["money-back-button-text"]}`}>
          {l10n.getString("money-back-guarantee-box-text")}{" "}
          <span>
            <img className={styles["check"]} src={check.src} />
          </span>
        </div>
      </Card>
    </ContainerButton>
  );
}

interface FooterTextProps {
  showStoreDisclaimer: boolean;
  showCreditBuilderDisclaimer: boolean;
  showMoneyBackGuarantee: boolean;
}

function FooterText({
  showStoreDisclaimer,
  showCreditBuilderDisclaimer,
  showMoneyBackGuarantee,
}: FooterTextProps) {
  const showFreePromo = useSelector(selectPromo(FREE_BASIC_DISCOUNTED_PREMIUM));

  return (
    <>
      {showMoneyBackGuarantee && <MoneyBackGuarantee />}
      {showCreditBuilderDisclaimer && (
        <div className={`text:mini ${styles["select-product-footer-text"]}`}>
          {showFreePromo ? "**" : "*"}
          {l10n.getString("credit-line-disclaimer")}
        </div>
      )}
      {showStoreDisclaimer && <StoreDisclaimer />}
    </>
  );
}

function addProductToBag(dispatch, preview, product) {
  dispatch(
    updateBag({
      items: [{ product, quantity: 1 }],
      autoRenew: {
        status: preview.autoRenew?.status,
      },
    })
  );
}

const plans: Plan[] = ["basic", "premium"];

type Comparison = {
  title: string;
  items: Record<Plan, string>;
  subtitle?: string;
};

interface SelectPlanPageProps extends PageRegistration {
  showNavBar?: boolean;
  isRenewal?: boolean;
  onPremium?: boolean;
  showStoreDisclaimer?: boolean;
  showDispute?: boolean;
  selectedGoal?: Goal;
  showMoneyBackGuarantee?: boolean;
  onGoalChange?(goal: Goal): void;
  showBack?: boolean;
}

export default function SelectPlanPage({
  configFlow,
  page,
  showNavBar = true,
  isRenewal = false,
  onPremium = false,
  showStoreDisclaimer = false,
  selectedGoal: _selectedGoal,
  showMoneyBackGuarantee = false,
  onGoalChange,
  showBack = false,
  Button,
}: SelectPlanPageProps) {
  const dispatch = useDispatch();
  const error = useErrorConsumer();

  const showPhoneSupport = useSelector(selectShowPhoneSupport());

  const preview = useSelector((state) => state.shopping.checkoutPreview);
  const promoByTier = useSelector(selectCheckoutPromos());

  const debtConsolidation = useSelector(
    selectFeatureFlag("debt_consolidation")
  );

  const ultimatePreview = useSelector(selectCheckoutPreview("AV6ZCTKV"));

  const [onboardingCopyVariant] = useSelector(
    selectExperimentVariant.load("onboardingCopy")
  );

  const defaultSelectPremiumExperiment =
    onPremium ||
    preview?.order.orderItems[0].product.plan ===
      web.public_.SubscriptionPlan.PREMIUM
      ? "premium"
      : "basic";

  const defaultSelectedPlan = ultimatePreview
    ? "ultimate"
    : defaultSelectPremiumExperiment;

  const [selectedGoal, setSelectedGoal] = useStateOrBind<Goal>(null, [
    _selectedGoal,
    onGoalChange,
  ]);

  const [selected, setSelected] = useState<Plan>(defaultSelectedPlan);
  const productByPlan = {
    basic: useSelector(selectProductForPlan("BASIC")),
    premium: useSelector(selectProductForPlan("PREMIUM")),
    ultimate: useSelector(selectProductForPlan("ULTIMATE")),
  };

  const asterisks = promoByTier.basic ? "**" : "*";

  const [seeMore, setSeeMore] = useState<boolean>(false);

  const comparisonRows = [
    {
      title: l10n.getString("credit-reports-scores-title"),
      items: {
        basic: l10n.getString("one-bureau"),
        premium: l10n.getString("three-bureaus"),
        ultimate: l10n.getString("three-bureaus"),
      },
    },
    {
      title: l10n.getString("build-payment-history-title"),
      items: {
        basic: "",
        premium: "",
        ultimate: "",
      },
    },
    {
      title: l10n.getString("lower-credit-utilization-title"),
      items: {
        basic: "",
        premium: "",
        ultimate: "",
      },
    },
    {
      title: l10n.getString("rent-reporting-benefit-title"),
      items: {
        basic: "",
        premium: "",
        ultimate: "",
      },
    },
    {
      title: `${l10n.getString("reported-credit-line-title")}${asterisks}`,
      items: {
        basic: "$750",
        premium: "$2500",
        ultimate: "$3500",
      },
    },
  ] as Comparison[];

  const additionalComparisonRows = [
    {
      title: l10n.getString("identity-theft-insurance-title"),
      items: {
        basic: "",
        premium: "",
        ultimate: "",
      },
    },
    {
      title: l10n.getString("personal-data-protection-title"),
      subtitle: l10n.getString("personal-data-protection-subtitle"),
      items: {
        basic: "",
        premium: "",
        ultimate: "",
      },
    },
  ] as Comparison[];

  const notLoaded = !productByPlan.basic;

  const selectedProduct = useMemo(() => productByPlan[selected], [selected]);

  useEffect(() => {
    if (!notLoaded) dispatch(updateCatalog());
    if (plans.indexOf("ultimate") === -1) plans.push("ultimate");
  }, []);

  useEffect(() => {
    if (preview && selectedProduct) {
      addProductToBag(dispatch, preview, selectedProduct);
    }
  }, [preview, selectedProduct]);

  useEffect(() => {
    if (selectedProduct) {
      dispatch(
        fetchCheckoutPreview(selectedProduct.token, { isRenewal })
      ).catch((e) => {
        error.throw(e);
      });
    }
  }, [selectedProduct]);

  configFlow
    .button(
      <Button
        className={styles["continue-button"]}
        disabled={selectedGoal === "build" && !selected}
        onClick={(e) => {
          if (selectedGoal === "borrow") {
            e.preventDefault();
            Router.replace("/dashboard/store/no-offers");

            setTimeout(() => {
              setSelectedGoal("build");
            }, 500);
            return;
          }

          analytics.only("Google Tag Manager").convert("Select Plan");

          track.tap(
            `CA - ${isRenewal ? "Renewal" : "Activation"} - Select Plan`,
            {
              planName: selected,
            }
          );
        }}
      >
        {(() => {
          if (!debtConsolidation) return l10n.getString("continue");
          if (selectedGoal === "build") {
            if (selected === "basic") return "Credit Builder Basic";
            if (selected === "premium") return "Credit Builder Premium";
          }
          if (selectedGoal === "borrow") return "Debt Consolidation Loans";
          return l10n.getString("continue");
        })()}
      </Button>,
      [selected, selectedGoal]
    )
    .bottom(
      showPhoneSupport && (
        <div className="mt-1 column-center">
          <PhoneDuringOnboardingSection />
        </div>
      ),
      [showPhoneSupport]
    );

  if (notLoaded) return null;

  let headerText;
  let planDescription;

  switch (onboardingCopyVariant) {
    case "credit_plus":
      headerText = l10n.getString("credit-plus-select-plan-title");
      planDescription = l10n.getString("credit-plus-duration-desc");
      break;
    case "membership":
      headerText = l10n.getString("membership-select-plan-title");
      planDescription = l10n.getString("membership-duration-desc");
      break;
    default:
      headerText = l10n.getString("select-plan-title");
      planDescription = l10n.getString("duration-desc");
      break;
  }

  return (
    <PageAlign
      className={styles["page-align-no-padding"]}
      scroll
      overscroll="120px"
    >
      {showNavBar && (
        <StreamlinedHeader displayBackButton={showBack} page={page} />
      )}
      <div className={styles["select-plan"]}>
        <header
          className={`text:heading-3 pt-2 ${styles["page-header"]}`}
          data-cy="select-plan-title"
        >
          {headerText}
        </header>

        {/* LOAN EXPERIMENT - GOAL SELECTOR */}
        {debtConsolidation && (
          <div className={styles["comparison-table"]}>
            <div className={styles["table-button-wrapper"]}>
              {goals.map((goal) => (
                <KButton
                  key={goal}
                  className={styles["select-goal-button"]}
                  data-cy={`goal-option-${goal}`}
                  size="full-width"
                  variant={
                    selectedGoal === goal ? "primary-outline" : "grey-outline"
                  }
                  onClick={() => {
                    setSelectedGoal(goal);
                    track(
                      `Debt Consolidation - Select Goal ${capitalize(
                        goal
                      )}` as keyof Events
                    );
                  }}
                >
                  <div
                    className={combineClasses(
                      "text:large+",
                      styles["select-goal-button-text"],
                      selectedGoal === goal && styles["highlighted-button-text"]
                    )}
                  >
                    {l10n.getString(`${goal}-goal-shortname`)}
                  </div>
                </KButton>
              ))}
            </div>
          </div>
        )}

        {/* PLAN DESCRIPTION */}
        <div className={`text:large ${styles["both-plans-desc"]}`}>
          {planDescription}
        </div>
        <StreamlinedPaymentPromoCard />

        {/* BUILDER PLANS */}
        {(selectedGoal === "build" || !debtConsolidation) && (
          <div className={styles["comparison-table"]}>
            <div className={styles["table-button-wrapper"]}>
              {plans.map((plan) => (
                <KButton
                  key={plan}
                  className={styles["select-plan-button"]}
                  data-cy={`plan-option-${plan}`}
                  size="full-width"
                  variant={selected === plan ? "primary" : "grey-outline"}
                  onClick={() => {
                    setSelected(plan);
                    track("CA Plan Selection - Select Product: Select", {
                      bundle: plan,
                    });
                  }}
                >
                  <div
                    className={combineClasses(
                      "text:large+",
                      styles["select-plan-button-text"],
                      selected === plan && styles["highlighted-button-text"]
                    )}
                  >
                    {l10n.getString(`${plan}-plan-shortname`)}
                  </div>
                </KButton>
              ))}
            </div>
            <div className={`text:mini ${styles["comparison-row-title"]}`}>
              {l10n.getString(
                promoByTier.basic
                  ? "monthly-price-title-promo"
                  : "monthly-price-title"
              )}
            </div>
            <div className={`text:regular+ ${styles["comparison-items"]}`}>
              {Object.entries(productByPlan).map(([plan, product]) => {
                const planDiscount =
                  promoByTier[plan]?.monthlyCreditAmountCents ?? 0;
                return (
                  <div
                    key={plan}
                    className={combineClasses(
                      "text:regular+",
                      styles["comparison-item"],
                      selected === plan && styles["item-highlighted"]
                    )}
                  >
                    {format.money(product?.monthlyPaymentCents - planDiscount)}{" "}
                    {planDiscount > 0 && (
                      <Strike className="color:moderate-weak">
                        {format.money(product?.monthlyPaymentCents)}
                      </Strike>
                    )}
                  </div>
                );
              })}
            </div>
            <Line />
            {comparisonRows.map(({ title, items }, idx) => (
              <ComparisonRow
                key={title}
                title={title}
                items={items}
                selected={selected}
                separator={idx < comparisonRows.length - 1}
              />
            ))}
            {seeMore && (
              <>
                <Line />
                {additionalComparisonRows.map(
                  ({ title, subtitle, items }, idx) => (
                    <ComparisonRow
                      key={title}
                      title={title}
                      subtitle={subtitle}
                      items={items}
                      selected={selected}
                      separator={idx < additionalComparisonRows.length - 1}
                    />
                  )
                )}
              </>
            )}
            <KButton
              size="small"
              variant="text-underline"
              onClick={() => {
                setSeeMore(!seeMore);
              }}
            >
              {seeMore
                ? l10n.getString("see-less")
                : l10n.getString("see-more")}
            </KButton>
          </div>
        )}

        {/* LOAN EXPERIMENT – LOAN PLANS */}
        {selectedGoal === "borrow" && (
          <div className={styles["comparison-table"]}>
            <div className={`text:mini ${styles["comparison-row-title"]}`}>
              {l10n.getString("interest-rate")}
            </div>
            <div
              className={combineClasses(
                "text:regular+",
                styles["comparison-item-solo"]
              )}
            >
              7.90% - 29.99%
            </div>
            <Line />
            <div className={`text:mini ${styles["comparison-row-title"]}`}>
              {l10n.getString("credit-limit")}
            </div>
            <div
              className={combineClasses(
                "text:regular+",
                styles["comparison-item-solo"]
              )}
            >
              {l10n.getString("up-to")} $4000
            </div>
            <Line />
            <div className={`text:mini ${styles["comparison-row-title"]}`}>
              {l10n.getString("automatically-pays-eligible-cards")}
            </div>
            <div
              className={combineClasses(
                "text:regular+",
                styles["comparison-item-solo"]
              )}
            >
              
            </div>
            <Line />
            <div className={`text:mini ${styles["comparison-row-title"]}`}>
              {l10n.getString("credit-scores-and-reports")}
            </div>
            <div
              className={combineClasses(
                "text:regular+",
                styles["comparison-item-solo"]
              )}
            >
              {l10n.getString("one-bureau")}
            </div>
          </div>
        )}

        {/* BACK TO REGULAR PROGRAMMING */}
        <FooterText
          showStoreDisclaimer={showStoreDisclaimer}
          showCreditBuilderDisclaimer={selectedGoal === "build"}
          showMoneyBackGuarantee={showMoneyBackGuarantee}
        />
      </div>
    </PageAlign>
  );
}

interface ComparisonRowProps extends Comparison {
  selected: string;
  subtitle?: string;
  separator?: boolean;
}

function ComparisonRow({
  title,
  items,
  selected,
  subtitle = "",
  separator = false,
}: ComparisonRowProps) {
  return (
    <>
      <div className={`text:mini ${styles["comparison-row-title"]}`}>
        {title}
      </div>
      {subtitle && (
        <div className="text:mini text-center color:moderate pb-2">
          {subtitle}
        </div>
      )}
      <div className={`text:regular+ ${styles["comparison-items"]}`}>
        {plans.map((plan) => (
          <div
            key={plan}
            className={combineClasses(
              "text:regular+",
              styles["comparison-item"],
              selected === plan && styles["item-highlighted"]
            )}
          >
            {items[plan]}
          </div>
        ))}
      </div>
      {separator && <Line />}
    </>
  );
}
